import { useContext } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import { useLatestEntity, useRealtimeSubscription, useThunkDispatch } from '@domain-group/react-hooks'

import RealtimeContext from '@contexts/realtimeContext'

import useSelector from '@hooks/useSelector'

import type { PublicationBookingItemModel } from '@models/types'

import * as publicationBookingItemActions from '@redux/modules/publicationBookingItem'

import type { AppDispatch } from '@redux/store'

export const generateUrls = () => ({ cancelledItemsUrl: '#/newsDesk/cancelledItems' })

type BulkDeletePublicationBookingItemsPayload = {
  bulkDeleteParams: { publicationBookingItemIds: number[] },
  dispatch: AppDispatch,
}

const bulkDeletePublicationBookingItems = (params: BulkDeletePublicationBookingItemsPayload) => {
  const { bulkDeleteParams, dispatch } = params

  const { bulkDeletePublicationBookingItems: bulkDeleteFn } = publicationBookingItemActions

  // @ts-expect-error TS2345
  return dispatch(bulkDeleteFn(bulkDeleteParams))
}

type CreatePublicationBookingItemParams = {
  dispatch: AppDispatch,
  publicationBookingItemParams: Partial<PublicationBookingItemModel>,
  requestOptions?: object,
}

const createPublicationBookingItem = (params: CreatePublicationBookingItemParams) => {
  const { dispatch, publicationBookingItemParams, requestOptions } = params
  const { createPublicationBookingItem: createFn } = publicationBookingItemActions

  return dispatch(createFn(publicationBookingItemParams, requestOptions))
}

type UpdatePublicationBookingItemParams = {
  dispatch: AppDispatch,
  publicationBookingItem: PublicationBookingItemModel,
  publicationBookingItemParams: Partial<PublicationBookingItemModel>,
  requestOptions?: object,
}

const updatePublicationBookingItem = (params: UpdatePublicationBookingItemParams) => {
  const {
    dispatch, publicationBookingItem, publicationBookingItemParams, requestOptions,
  } = params
  const { updatePublicationBookingItem: updateFn } = publicationBookingItemActions

  const itemParams = {
    id: publicationBookingItem.id,
    ...publicationBookingItemParams,
  }

  return dispatch(updateFn(itemParams, requestOptions))
}

type DeletePublicationBookingItemParams = {
  dispatch: AppDispatch,
  publicationBookingItem: PublicationBookingItemModel,
}

const deletePublicationBookingItem = (params: DeletePublicationBookingItemParams) => {
  const {
    dispatch, publicationBookingItem,
  } = params
  const { deletePublicationBookingItem: deleteFn } = publicationBookingItemActions

  return dispatch(deleteFn(publicationBookingItem))
}

type CancelPublicationBookingItemParams = {
  dispatch: AppDispatch,
  publicationBookingItem: PublicationBookingItemModel,
  requestOptions?: object,
}

const cancelPublicationBookingItem = (params: CancelPublicationBookingItemParams) => {
  const {
    dispatch, publicationBookingItem, requestOptions,
  } = params
  const { cancelPublicationBookingItem: cancelFn } = publicationBookingItemActions

  return dispatch(cancelFn(publicationBookingItem, requestOptions))
}

type RemovePublicationBookingItemParams = {
  dispatch: AppDispatch,
  publicationBookingItem: PublicationBookingItemModel,
  requestOptions?: object,
}

const removePublicationBookingItem = (params: RemovePublicationBookingItemParams) => {
  const {
    dispatch, publicationBookingItem, requestOptions,
  } = params
  const { removePublicationBookingItem: removeFn } = publicationBookingItemActions

  return dispatch(removeFn(publicationBookingItem, requestOptions))
}

type PublicationBookingItemDeletePayload = {
  filter: {
    [key: string]: 'delete',
  },
  payload: { id: number },
}

type ProcessingCompleteParams = {
  data: PublicationBookingItemDeletePayload,
  dispatch: AppDispatch,
}

const processingComplete = (params: ProcessingCompleteParams) => {
  const { data, dispatch } = params

  const deletedPublicationBookingItemId = digObject(data, 'payload.id')

  const { removePublicationBookingItemFromStore: removeFromStoreFn } = publicationBookingItemActions
  return dispatch(removeFromStoreFn(deletedPublicationBookingItemId))
}

export const useRelations = (publicationBookingItem: Partial<PublicationBookingItemModel> = {}) => {
  const {
    artwork_id,
    order_id,
    order_item_id,
    publication_booking_id,
    status_id,
  } = publicationBookingItem

  const {
    agencies,
    artworks,
    campaigns,
    orders,
    orderItems,
    publicationBookings,
    serviceProducts,
    statuses,
  } = useSelector(reduxState => reduxState.entities)

  const artwork = artwork_id && artworks[artwork_id] ? artworks[artwork_id] : {}
  const order = order_id && orders[order_id] ? orders[order_id] : {}
  // @ts-expect-error TS2339
  const agency = order.agency_id && agencies[order.agency_id] ? agencies[order.agency_id] : {}
  // @ts-expect-error TS2339
  const campaign = order && campaigns[order.campaign_id] ? campaigns[order.campaign_id] : {}

  const orderItem = order_item_id && orderItems[order_item_id] ? orderItems[order_item_id] : {}
  const publicationBooking = publication_booking_id && publicationBookings[publication_booking_id]
    ? publicationBookings[publication_booking_id]
    : {}
  // @ts-expect-error TS2339
  const serviceProduct = orderItem.service_product_id && serviceProducts[orderItem.service_product_id]
    // @ts-expect-error TS2339
    ? serviceProducts[orderItem.service_product_id]
    : {}
  const status = status_id && statuses[status_id] ? statuses[status_id] : {}

  return {
    agency,
    artwork,
    campaign,
    order,
    orderItem,
    publicationBooking,
    serviceProduct,
    status,
  }
}

function usePublicationBookingItem(initEntity: Partial<PublicationBookingItemModel> = {}) {
  // @ts-expect-error TS2322
  const {
    entity: publicationBookingItem,
  }: { entity: PublicationBookingItemModel} = useLatestEntity(initEntity, 'publicationBookingItems')

  const dispatch = useThunkDispatch()

  const {
    agency,
    artwork,
    campaign,
    order,
    orderItem,
    publicationBooking,
    serviceProduct,
  } = useRelations(publicationBookingItem)

  const { loading } = useSelector(reduxState => reduxState.publicationBookingItems)

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  // Listen for the cancellation of a booking
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: PublicationBookingItemDeletePayload) => processingComplete({ data, dispatch }),
    },
    channelName: 'publicationBookingItems',
    clientRef,
    eventName: `publicationBookingItem.${publicationBookingItem.id}.delete.complete`,
  }, [publicationBookingItem.id])

  return {
    agency,
    artwork,
    callbacks: {
      bulkDeletePublicationBookingItems: (
        bulkDeleteParams: { publicationBookingItemIds: number[] },
      ) => bulkDeletePublicationBookingItems({ bulkDeleteParams, dispatch }),
      cancelPublicationBookingItem: (entityOptions?: object) => (
        cancelPublicationBookingItem({
          dispatch,
          publicationBookingItem,
          requestOptions: entityOptions,
        })
      ),
      createPublicationBookingItem: (
        publicationBookingItemParams: Partial<PublicationBookingItemModel>,
        entityOptions?: object,
      ) => (
        createPublicationBookingItem({
          dispatch,
          publicationBookingItemParams,
          requestOptions: entityOptions,
        })
      ),
      deletePublicationBookingItem: () => (
        deletePublicationBookingItem({
          dispatch,
          publicationBookingItem,
        })
      ),
      removeOrDeletePublicationBookingItem: (entityOptions?: object) => {
        // Do not delete if booking is confirmed
        if (publicationBookingItem.booking_confirmed){
          return removePublicationBookingItem({
            dispatch,
            publicationBookingItem,
            requestOptions: entityOptions,
          })
        }

        return deletePublicationBookingItem({
          dispatch,
          publicationBookingItem,
        })
      },
      removePublicationBookingItem: (entityOptions?: object) => (
        removePublicationBookingItem({
          dispatch,
          publicationBookingItem,
          requestOptions: entityOptions,
        })
      ),
      updatePublicationBookingItem: (
        publicationBookingItemParams: Partial<PublicationBookingItemModel>,
        entityOptions?: object,
      ) => (
        updatePublicationBookingItem({
          dispatch,
          publicationBookingItem,
          publicationBookingItemParams,
          requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    loading,
    order,
    orderItem,
    publicationBooking,
    publicationBookingItem,
    serviceProduct,
    urls: generateUrls(),
  }
}

export type UsePublicationBookingItemPayload = ReturnType<typeof usePublicationBookingItem>

export default usePublicationBookingItem
